import React, { useState, useCallback } from 'react';
import { Save, RefreshCw, Wallet, Download, Calculator, PlusCircle, Trash2, Settings, CreditCard, TrendingUp, ShoppingCart, PiggyBank } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { initialFormData } from '../../FamilyBudgetCalculatorPage';

// Импорт секций формы
import BudgetInfoSection from './BudgetInfoSection';
import IncomeSection from './IncomeSection';
import ExpenseSection from './ExpenseSection';
import SavingsSection from './SavingsSection';
import InvestmentsSection from './InvestmentsSection';
import DistributionSection from './DistributionSection';
import SettingsSection from './SettingsSection';

// Импорт утилитарных функций
import { isPositiveNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета семейного бюджета
 */
const BudgetForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  incomeCategories,
  expenseCategories,
  russianRegions,
  saveBudget,
  activeTab,
  setActiveTab
}) => {
  
  // Состояние для имени сохраняемого бюджета
  const [saveBudgetName, setSaveBudgetName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    // Разделение путей вложенных объектов (например "savings.emergencyFund.enabled")
    if (name.includes('.')) {
      const nameParts = name.split('.');
      setFormData(prev => {
        const newState = { ...prev };
        let current = newState;
        
        // Проходим по всем частям пути, кроме последней
        for (let i = 0; i < nameParts.length - 1; i++) {
          current = current[nameParts[i]];
        }
        
        // Устанавливаем значение для последней части
        current[nameParts[nameParts.length - 1]] = fieldValue;
        return newState;
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: fieldValue }));
    }

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Добавление нового элемента дохода
  const addIncomeItem = () => {
    setFormData(prev => {
      const newItem = {
        id: Date.now().toString(),
        name: 'Новый доход',
        amount: '0',
        category: 'other',
        recurring: true,
        frequency: 'monthly'
      };
      
      return {
        ...prev,
        incomes: [...prev.incomes, newItem]
      };
    });
  };

  // Добавление нового элемента расхода
  const addExpenseItem = () => {
    setFormData(prev => {
      const newItem = {
        id: Date.now().toString(),
        name: 'Новый расход',
        amount: '0',
        category: 'other',
        recurring: true,
        frequency: 'monthly',
        essential: false
      };
      
      return {
        ...prev,
        expenses: [...prev.expenses, newItem]
      };
    });
  };

  // Добавление новой финансовой цели
  const addGoal = () => {
    setFormData(prev => {
      const newGoal = {
        id: Date.now().toString(),
        name: 'Новая цель',
        target: '100000',
        current: '0',
        deadline: new Date(new Date().setMonth(new Date().getMonth() + 12)).toISOString().split('T')[0],
        priority: 'medium'
      };
      
      return {
        ...prev,
        savings: {
          ...prev.savings,
          goals: [...prev.savings.goals, newGoal]
        }
      };
    });
  };

  // Удаление элемента дохода
  const removeIncomeItem = (id) => {
    setFormData(prev => ({
      ...prev,
      incomes: prev.incomes.filter(item => item.id !== id)
    }));
  };

  // Удаление элемента расхода
  const removeExpenseItem = (id) => {
    setFormData(prev => ({
      ...prev,
      expenses: prev.expenses.filter(item => item.id !== id)
    }));
  };

  // Удаление финансовой цели
  const removeGoal = (id) => {
    setFormData(prev => ({
      ...prev,
      savings: {
        ...prev.savings,
        goals: prev.savings.goals.filter(goal => goal.id !== id)
      }
    }));
  };

  // Обновление поля элемента дохода
  const updateIncomeItem = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      incomes: prev.incomes.map(item => 
        item.id === id 
          ? { ...item, [field]: value } 
          : item
      )
    }));
  };

  // Обновление поля элемента расхода
  const updateExpenseItem = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      expenses: prev.expenses.map(item => 
        item.id === id 
          ? { ...item, [field]: value } 
          : item
      )
    }));
  };

  // Обновление поля финансовой цели
  const updateGoal = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      savings: {
        ...prev.savings,
        goals: prev.savings.goals.map(goal => 
          goal.id === id 
            ? { ...goal, [field]: value } 
            : goal
        )
      }
    }));
  };

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Проверка основной информации
    if (!formData.budgetName.trim()) {
      newErrors.budgetName = 'Введите название бюджета';
    }

    // Функция для проверки числовых значений
    const validateNumber = (value, errorKey, errorMessage) => {
      if (!isPositiveNumber(value)) {
        newErrors[errorKey] = errorMessage || 'Должно быть положительным числом';
      }
    };

    // Проверка доходов
    formData.incomes.forEach((income, index) => {
      if (!income.name.trim()) {
        newErrors[`income_${index}_name`] = 'Введите название дохода';
      }
      
      if (!isPositiveNumber(income.amount)) {
        newErrors[`income_${index}_amount`] = 'Сумма должна быть положительным числом';
      }
    });

    // Проверка расходов
    formData.expenses.forEach((expense, index) => {
      if (!expense.name.trim()) {
        newErrors[`expense_${index}_name`] = 'Введите название расхода';
      }
      
      if (!isPositiveNumber(expense.amount)) {
        newErrors[`expense_${index}_amount`] = 'Сумма должна быть положительным числом';
      }
    });

    // Проверка сбережений и целей
    if (formData.savings.emergencyFund.enabled) {
      validateNumber(
        formData.savings.emergencyFund.target, 
        'emergencyFundTarget', 
        'Целевая сумма НЗ должна быть положительным числом'
      );
      validateNumber(
        formData.savings.emergencyFund.current, 
        'emergencyFundCurrent', 
        'Текущая сумма НЗ должна быть положительным числом'
      );
    }
    
    if (formData.savings.retirementFund.enabled) {
      validateNumber(
        formData.savings.retirementFund.target, 
        'retirementFundTarget', 
        'Целевая сумма пенсионных накоплений должна быть положительным числом'
      );
      validateNumber(
        formData.savings.retirementFund.monthlyContribution, 
        'retirementFundContribution', 
        'Ежемесячный взнос должен быть положительным числом'
      );
    }
    
    formData.savings.goals.forEach((goal, index) => {
      if (!goal.name.trim()) {
        newErrors[`goal_${index}_name`] = 'Введите название цели';
      }
      
      validateNumber(
        goal.target, 
        `goal_${index}_target`, 
        'Целевая сумма должна быть положительным числом'
      );
      
      validateNumber(
        goal.current, 
        `goal_${index}_current`, 
        'Текущая сумма должна быть положительным числом'
      );
      
      // Проверка срока
      try {
        const deadline = new Date(goal.deadline);
        if (isNaN(deadline.getTime())) {
          newErrors[`goal_${index}_deadline`] = 'Некорректная дата';
        }
      } catch (e) {
        newErrors[`goal_${index}_deadline`] = 'Некорректная дата';
      }
    });
    
    // Проверка инвестиций
    if (formData.investments.enabled) {
      validateNumber(
        formData.investments.monthlyContribution, 
        'investmentsContribution', 
        'Ежемесячный взнос должен быть положительным числом'
      );
      
      validateNumber(
        formData.investments.expectedReturn, 
        'investmentsReturn', 
        'Ожидаемая доходность должна быть положительным числом'
      );
    }
    
    // Проверка распределения процентов (в сумме должно быть 100%)
    const distribution = formData.distribution;
    const totalPercentage = 
      parseFloat(distribution.savingsPercentage) + 
      parseFloat(distribution.entertainmentPercentage) + 
      parseFloat(distribution.essentialsPercentage) + 
      parseFloat(distribution.investmentsPercentage);
    
    if (Math.abs(totalPercentage - 100) > 0.1) {
      newErrors.distribution = 'Общая сумма распределения должна составлять 100%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет бюджета
  const calculateBudget = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Расчет бюджета ---
        
        // Конвертируем все числовые значения
        const parseNumber = (value) => {
          return parseFloat(String(value).replace(/,/g, '.')) || 0;
        };

        // Рассчитываем общий доход
        const totalIncome = formData.incomes.reduce((sum, income) => {
          const amount = parseNumber(income.amount);
          
          // Учет периодичности
          if (income.recurring) {
            return sum + amount;
          } else {
            // Для нерегулярных доходов делим на 12 для месячного бюджета
            if (formData.budgetPeriod === 'monthly') {
              return sum + amount / 12;
            } else {
              return sum + amount;
            }
          }
        }, 0);

        // Рассчитываем общий расход
        const totalExpenses = formData.expenses.reduce((sum, expense) => {
          const amount = parseNumber(expense.amount);
          
          // Учет периодичности
          if (expense.recurring) {
            return sum + amount;
          } else {
            // Для нерегулярных расходов делим на 12 для месячного бюджета
            if (formData.budgetPeriod === 'monthly') {
              return sum + amount / 12;
            } else {
              return sum + amount;
            }
          }
        }, 0);

        // Базовый баланс
        const balance = totalIncome - totalExpenses;
        
        // Рассчитываем расходы по категориям
        const expensesByCategory = {};
        formData.expenses.forEach(expense => {
          const amount = parseNumber(expense.amount);
          if (!expensesByCategory[expense.category]) {
            expensesByCategory[expense.category] = 0;
          }
          expensesByCategory[expense.category] += amount;
        });
        
        // Рассчитываем доходы по категориям
        const incomesByCategory = {};
        formData.incomes.forEach(income => {
          const amount = parseNumber(income.amount);
          if (!incomesByCategory[income.category]) {
            incomesByCategory[income.category] = 0;
          }
          incomesByCategory[income.category] += amount;
        });
        
        // Расчет процента сбережений и инвестиций от дохода
        const savingsAmount = (balance > 0) 
          ? (balance * parseNumber(formData.distribution.savingsPercentage) / 100) 
          : 0;
        
        const investmentsAmount = (balance > 0) 
          ? (balance * parseNumber(formData.distribution.investmentsPercentage) / 100) 
          : 0;
        
        // Расчет оставшегося дохода после сбережений и инвестиций
        const remainingIncome = balance - savingsAmount - investmentsAmount;
        
        // Расчет времени достижения целей
        const goals = formData.savings.goals.map(goal => {
          const targetAmount = parseNumber(goal.target);
          const currentAmount = parseNumber(goal.current);
          const remaining = targetAmount - currentAmount;
          
          // Месяцы до достижения цели (если есть положительный баланс)
          const monthsToGoal = savingsAmount > 0 
            ? Math.ceil(remaining / savingsAmount) 
            : Infinity;
          
          return {
            ...goal,
            targetAmount,
            currentAmount,
            remaining,
            monthsToGoal,
            achievable: savingsAmount > 0
          };
        });
        
        // Расчет для чрезвычайного фонда
        let emergencyFund = { ...formData.savings.emergencyFund };
        if (emergencyFund.enabled) {
          const targetAmount = parseNumber(emergencyFund.target);
          const currentAmount = parseNumber(emergencyFund.current);
          const remaining = targetAmount - currentAmount;
          
          // Месяцы до создания НЗ
          const monthsToTarget = savingsAmount > 0 
            ? Math.ceil(remaining / savingsAmount) 
            : Infinity;
          
          emergencyFund = {
            ...emergencyFund,
            targetAmount,
            currentAmount,
            remaining,
            monthsToTarget,
            achievable: savingsAmount > 0
          };
        }
        
        // Расчет для пенсионных накоплений
        let retirementFund = { ...formData.savings.retirementFund };
        if (retirementFund.enabled) {
          const targetAmount = parseNumber(retirementFund.target);
          const currentAmount = parseNumber(retirementFund.current);
          const monthlyContribution = parseNumber(retirementFund.monthlyContribution);
          const remaining = targetAmount - currentAmount;
          
          // Месяцы до цели при фиксированном взносе
          const monthsToTarget = monthlyContribution > 0 
            ? Math.ceil(remaining / monthlyContribution) 
            : Infinity;
          
          retirementFund = {
            ...retirementFund,
            targetAmount,
            currentAmount,
            monthlyContribution,
            remaining,
            monthsToTarget,
            achievable: monthlyContribution > 0
          };
        }
        
        // Расчет для инвестиций
        let investments = { ...formData.investments };
        if (investments.enabled) {
          const portfolioValue = parseNumber(investments.portfolioValue);
          const monthlyContribution = parseNumber(investments.monthlyContribution);
          const expectedReturn = parseNumber(investments.expectedReturn) / 100;
          
          // Прогноз стоимости через 1, 5, 10 и 20 лет
          const projection = {};
          
          [1, 5, 10, 20].forEach(years => {
            const months = years * 12;
            let futureValue = portfolioValue;
            
            // Расчет по формуле сложных процентов с ежемесячными взносами
            for (let i = 0; i < months; i++) {
              futureValue = futureValue * (1 + expectedReturn / 12) + monthlyContribution;
            }
            
            projection[years] = futureValue;
          });
          
          investments = {
            ...investments,
            portfolioValue,
            monthlyContribution,
            expectedReturn,
            projection
          };
        }
        
        // Расчет соотношения основных категорий расходов
        const essentialExpenses = formData.expenses
          .filter(expense => expense.essential)
          .reduce((sum, expense) => sum + parseNumber(expense.amount), 0);
        
        const nonEssentialExpenses = totalExpenses - essentialExpenses;
        
        // Доля основных категорий в расходах
        const essentialRatio = totalExpenses > 0 ? essentialExpenses / totalExpenses : 0;
        const nonEssentialRatio = totalExpenses > 0 ? nonEssentialExpenses / totalExpenses : 0;
        
        // Расчет финансовых показателей
        const savingsRate = totalIncome > 0 ? savingsAmount / totalIncome : 0;
        const expenseRatio = totalIncome > 0 ? totalExpenses / totalIncome : 0;
        
        // Достаточность дохода (коэффициент)
        const incomeToExpenseRatio = totalExpenses > 0 ? totalIncome / totalExpenses : 0;
        
        // Оценка общего финансового здоровья (от 0 до 100)
        let financialHealthScore = 0;
        
        // Компоненты оценки:
        // 1. Положительный баланс (до 30 баллов)
        const balanceScore = balance > 0 ? Math.min(30, 30 * (balance / totalIncome)) : 0;
        
        // 2. Достаточность дохода (до 25 баллов)
        const incomeScore = Math.min(25, 25 * (incomeToExpenseRatio > 1 ? (incomeToExpenseRatio - 1) : 0));
        
        // 3. Наличие сбережений (до 20 баллов)
        const savingsScore = emergencyFund.enabled ? Math.min(20, 20 * (emergencyFund.currentAmount / emergencyFund.targetAmount)) : 0;
        
        // 4. Структура расходов (до 15 баллов)
        const expenseStructureScore = essentialRatio <= 0.7 ? 15 * (1 - essentialRatio / 0.7) : 0;
        
        // 5. Наличие инвестиций (до 10 баллов)
        const investmentScore = investments.enabled ? 10 : 0;
        
        // Итоговая оценка
        financialHealthScore = balanceScore + incomeScore + savingsScore + expenseStructureScore + investmentScore;
        
        // Подготавливаем статус и рекомендации
        let status = 'neutral';
        let recommendations = [];
        
        if (financialHealthScore >= 80) {
          status = 'excellent';
          recommendations = [
            'Продолжайте в том же духе, ваш бюджет хорошо сбалансирован',
            'Рассмотрите возможность увеличения инвестиций для роста капитала',
            'Подумайте о диверсификации ваших доходов'
          ];
        } else if (financialHealthScore >= 60) {
          status = 'good';
          recommendations = [
            'Ваш бюджет в хорошем состоянии, но есть возможности для улучшения',
            'Постарайтесь увеличить процент сбережений',
            'Пересмотрите необязательные расходы'
          ];
        } else if (financialHealthScore >= 40) {
          status = 'fair';
          recommendations = [
            'Ваш бюджет требует внимания и корректировок',
            'Работайте над снижением доли обязательных расходов',
            'Найдите способы увеличить доход или сократить расходы'
          ];
        } else {
          status = 'poor';
          recommendations = [
            'Ваш бюджет нуждается в серьезной корректировке',
            'Сосредоточьтесь на снижении расходов и погашении долгов',
            'Рассмотрите дополнительные источники дохода',
            'Создайте план действий для выхода из финансовых затруднений'
          ];
        }
        
        // Расчет НДФЛ, если включено
        let taxAmount = 0;
        if (formData.settings.includeTaxes) {
          const taxRate = parseNumber(formData.settings.taxRate) / 100;
          taxAmount = totalIncome * taxRate;
        }
        
        // Формирование итоговых результатов
        const calculatedResults = {
          // Основные показатели
          totalIncome,
          totalExpenses,
          balance,
          disposableIncome: remainingIncome,
          
          // Категории
          incomesByCategory,
          expensesByCategory,
          
          // Сбережения и инвестиции
          savingsAmount,
          investmentsAmount,
          emergencyFund,
          retirementFund,
          goals,
          investments,
          
          // Структура расходов
          essentialExpenses,
          nonEssentialExpenses,
          essentialRatio,
          nonEssentialRatio,
          
          // Финансовые коэффициенты
          savingsRate,
          expenseRatio,
          incomeToExpenseRatio,
          
          // Оценка и рекомендации
          financialHealthScore,
          status,
          recommendations,
          
          // Налоги
          taxAmount,
          afterTaxIncome: totalIncome - taxAmount,
          
          // Дополнительная информация
          budgetPeriod: formData.budgetPeriod,
          basedOn: {
            region: formData.region,
            familyMembers: formData.familyMembers,
            hasChildren: formData.hasChildren,
            childrenCount: formData.childrenCount,
          }
        };

        setResults(calculatedResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData(initialFormData);
    setResults(null);
    setErrors({});
    setSaveBudgetName('');
    setShowSaveDialog(false);
  };

  // Экспорт бюджета в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Функция форматирования числа для CSV
    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    // Подготавливаем строки для CSV
    const infoRows = [
      ['Наименование бюджета:', formData.budgetName],
      ['Тип бюджета:', formData.budgetType === 'family' ? 'Семейный' : formData.budgetType === 'personal' ? 'Личный' : 'Семейная пара'],
      ['Период:', formData.budgetPeriod === 'monthly' ? 'Ежемесячный' : 'Годовой'],
      ['Количество членов семьи:', formData.familyMembers.toString()],
      ['Регион:', russianRegions.find(r => r.id === formData.region)?.name || formData.region],
      [''],  // Пустая строка для разделения
    ];

    // Доходы
    infoRows.push(['ДОХОДЫ']);
    formData.incomes.forEach(income => {
      const category = incomeCategories.find(c => c.id === income.category)?.name || income.category;
      infoRows.push([income.name, category, formatCsvNumber(parseFloat(income.amount))]);
    });
    infoRows.push(['Итого доходов:', '', formatCsvNumber(results.totalIncome)]);
    infoRows.push(['']);

    // Расходы
    infoRows.push(['РАСХОДЫ']);
    formData.expenses.forEach(expense => {
      const category = expenseCategories.find(c => c.id === expense.category)?.name || expense.category;
      infoRows.push([expense.name, category, formatCsvNumber(parseFloat(expense.amount))]);
    });
    infoRows.push(['Итого расходов:', '', formatCsvNumber(results.totalExpenses)]);
    infoRows.push(['']);

    // Баланс
    infoRows.push(['БАЛАНС:', '', formatCsvNumber(results.balance)]);
    infoRows.push(['']);

    // Сбережения и финансовые цели
    if (formData.savings.goals.length > 0) {
      infoRows.push(['ФИНАНСОВЫЕ ЦЕЛИ']);
      formData.savings.goals.forEach(goal => {
        infoRows.push([
          goal.name, 
          `Цель: ${formatCsvNumber(parseFloat(goal.target))}`, 
          `Накоплено: ${formatCsvNumber(parseFloat(goal.current))}`,
          `Осталось: ${formatCsvNumber(parseFloat(goal.target) - parseFloat(goal.current))}`
        ]);
      });
      infoRows.push(['']);
    }

    // Оценка финансового здоровья
    infoRows.push(['ОЦЕНКА ФИНАНСОВОГО СОСТОЯНИЯ']);
    infoRows.push(['Оценка бюджета:', `${results.financialHealthScore.toFixed(0)} из 100`]);
    infoRows.push(['Статус:', results.status === 'excellent' ? 'Отличный' :
                          results.status === 'good' ? 'Хороший' :
                          results.status === 'fair' ? 'Удовлетворительный' : 'Требует внимания']);
    infoRows.push(['']);

    // Рекомендации
    infoRows.push(['РЕКОМЕНДАЦИИ']);
    results.recommendations.forEach((rec, index) => {
      infoRows.push([`${index + 1}. ${rec}`]);
    });

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + infoRows.map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'семейный_бюджет.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения бюджета
  const handleSaveBudget = () => {
    if (showSaveDialog) {
      const savedResult = saveBudget(saveBudgetName || formData.budgetName);
      if (savedResult) {
        alert(`Бюджет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveBudgetName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateBudget();
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="mb-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Wallet className="w-6 h-6 text-emerald-500" />
          Данные для расчета бюджета
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Навигация по вкладкам */}
        <div className="mb-6 bg-gray-50 dark:bg-gray-800/40 rounded-xl p-1 shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <div className="flex space-x-1">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg flex items-center transition-colors ${
                activeTab === 'overview' 
                  ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/50'
              }`}
            >
              <Wallet className="w-4 h-4 mr-2" />
              Общая информация
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('income')}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg flex items-center transition-colors ${
                activeTab === 'income' 
                  ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/50'
              }`}
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Доходы
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('expenses')}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg flex items-center transition-colors ${
                activeTab === 'expenses' 
                  ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/50'
              }`}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Расходы
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('savings')}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg flex items-center transition-colors ${
                activeTab === 'savings' 
                  ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/50'
              }`}
            >
              <PiggyBank className="w-4 h-4 mr-2" />
              Сбережения
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('investments')}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg flex items-center transition-colors ${
                activeTab === 'investments' 
                  ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/50'
              }`}
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Инвестиции
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg flex items-center transition-colors ${
                activeTab === 'settings' 
                  ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/50'
              }`}
            >
              <Settings className="w-4 h-4 mr-2" />
              Настройки
            </button>
          </div>
        </div>

        {/* Основная информация о бюджете */}
        {activeTab === 'overview' && (
          <BudgetInfoSection 
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            russianRegions={russianRegions}
          />
        )}

        {/* Доходы */}
        {activeTab === 'income' && (
          <IncomeSection 
            formData={formData}
            incomes={formData.incomes}
            updateIncomeItem={updateIncomeItem}
            removeIncomeItem={removeIncomeItem}
            addIncomeItem={addIncomeItem}
            incomeCategories={incomeCategories}
            errors={errors}
          />
        )}

        {/* Расходы */}
        {activeTab === 'expenses' && (
          <ExpenseSection 
            formData={formData}
            expenses={formData.expenses}
            updateExpenseItem={updateExpenseItem}
            removeExpenseItem={removeExpenseItem}
            addExpenseItem={addExpenseItem}
            expenseCategories={expenseCategories}
            errors={errors}
          />
        )}

        {/* Сбережения и финансовые цели */}
        {activeTab === 'savings' && (
          <SavingsSection 
            formData={formData}
            handleInputChange={handleInputChange}
            updateGoal={updateGoal}
            removeGoal={removeGoal}
            addGoal={addGoal}
            errors={errors}
          />
        )}

        {/* Инвестиции */}
        {activeTab === 'investments' && (
          <InvestmentsSection 
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}

        {/* Распределение бюджета */}
        {activeTab === 'overview' && (
          <DistributionSection 
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}

        {/* Настройки */}
        {activeTab === 'settings' && (
          <SettingsSection 
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}

        {/* Диалог сохранения бюджета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveBudgetName}
                onChange={(e) => setSaveBudgetName(e.target.value)}
                className="form-input flex-grow mr-3"
                defaultValue={formData.budgetName}
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveBudget}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Расчет...' : 'Рассчитать бюджет'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить бюджет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default BudgetForm;